import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'wouter';

import { useAccess, useHasAccess } from '../../hooks/useAccess';
import useElementSize from '../../hooks/useElementSize';
import {
    useCategoryUrlGenerator,
    useCollectionUrlGenerator,
    useFormatUrlGenerator,
} from '../../hooks/useUrlGenerator';

import Blocks from '../blocks/Blocks';
import PillButton from '../buttons/PillButton';
import PlayIcon from '../icons/PlayIcon';
import Background from '../partials/Background';
import Blocked from '../partials/Blocked';
import Broadcasts from '../partials/Broadcasts';
import Credits from '../partials/Credits';
import Image from '../partials/Image';
import Sponsor from '../partials/Sponsor';
import Surtitle from '../partials/Surtitle';
import Video from '../partials/Video';
import Body from '../typography/Body';
import HeaderTitle from '../typography/HeaderTitle';
import IndexDefaultHeader from './IndexDefaultHeader';

import styles from '../../styles/headers/collection-serie-header.module.css';

export interface CollectionSerieHeaderProps {
    type?: string | null;
    slug?: string | null;
    title?: string | null;
    surtitle?: string | null;
    subtitle?: string | null;
    description?: string | null;
    trailer?: Video | null;
    background?: Background | null;
    callToAction?: CallToAction | null;
    metadata?: CollectionMetadata | null;
    access?: Access | null;
    color?: string;
    image?: Image | null;
    currentDocument?: FullDocument | null;
    placeholder?: boolean;
    withoutImage?: boolean;
    className?: string | null;
    imageClassName?: string | null;
}

function CollectionSerieHeader({
    type,
    slug = null,
    title = null,
    surtitle = null,
    description = null,
    trailer = null,
    image = null,
    callToAction = null,
    metadata = null,
    access = null,
    placeholder = false,
    background = null,
    withoutImage = false,
    className = null,
    imageClassName = null,
}: CollectionSerieHeaderProps) {
    const url = useUrlGenerator();
    const formatUrl = useFormatUrlGenerator();
    const categoryUrl = useCategoryUrlGenerator();
    const collectionUrl = useCollectionUrlGenerator();
    const {
        brands,
        formats,
        categories,
        sponsors,
        credits = null,
        serieFormat = null,
        broadcasts = null,
    } = metadata || {};
    const finalAccess = useAccess({
        access,
    });
    const { blocked = false } = finalAccess || {};
    const unblocked = useHasAccess(finalAccess);
    const [brand = null] = brands || [];
    const { handle: brandHandle } = brand || {};
    const finalSurtitle = serieFormat || surtitle;
    const isPodcast = type !== null && type.indexOf('podcast') !== -1;

    const pills = [
        ...(formats || []).map(({ label, slug }) => ({
            label,
            href: formatUrl({ slug }),
        })),
        ...(categories || []).map(({ label, slug }) => ({
            label,
            href: categoryUrl({ slug }),
        })),
    ].filter(({ label }) => label !== 'Balado');
    const [sponsor = null] = sponsors || [];

    const { label: ctaLabel = null, url: ctaUrl = null } = callToAction || {};

    const imageWithBackground = background !== null && image !== null;
    const imageWithoutBackground = background === null && (image !== null || placeholder);
    const { ref: imageContainerRef, height: imageContainerHeight } = useElementSize({
        disabled: !imageWithoutBackground,
    });
    const { ref: imageRef, height: imageHeight } = useElementSize({
        disabled: !imageWithoutBackground,
    });

    const [trailerPlaying, setTrailerPlaying] = useState(false);
    const onClickPlayTrailer = () => {
        setTrailerPlaying(true);
    };
    const onTrailerEnded = () => {
        setTrailerPlaying(false);
    };

    const imageOffset =
        imageWithoutBackground && imageHeight > imageContainerHeight
            ? Math.round((imageHeight - imageContainerHeight) / 2)
            : null;

    const hasOnlyTitle = description === null && background === null && image === null;
    if (hasOnlyTitle && !placeholder) {
        return <IndexDefaultHeader brand={brand} title={title} className={className} />;
    }

    return (
        <div
            className={classNames([
                styles.container,
                styles[brandHandle],
                {
                    [styles.trailerVisible]: trailerPlaying,
                    [styles.placeholder]: placeholder,
                    [styles.imageWithBackground]: imageWithBackground,
                },
                className,
            ])}
        >
            {!withoutImage ? (
                <div
                    className={classNames([styles.imageContainer, imageClassName])}
                    ref={imageContainerRef}
                >
                    {background !== null ? (
                        <Background
                            {...background}
                            className={styles.background}
                            imageClassName={styles.backgroundImage}
                            pictureClassName={styles.backgroundPicture}
                        />
                    ) : null}

                    {image !== null || placeholder ? (
                        <div
                            ref={imageRef}
                            style={{
                                transform:
                                    imageOffset !== null ? `translateY(-${imageOffset}px)` : null,
                            }}
                        >
                            <Image
                                className={styles.image}
                                media={image}
                                size="responsive"
                                withPlaceholder={placeholder}
                                pictureClassName={styles.picture}
                            />
                        </div>
                    ) : null}

                    {trailer !== null ? (
                        <Video
                            media={trailer}
                            className={classNames([
                                styles.trailer,
                                {
                                    [styles.visible]: trailerPlaying,
                                },
                            ])}
                            playing={trailerPlaying}
                            onEnded={onTrailerEnded}
                            controls
                            nativeControls
                            withoutBlock
                        />
                    ) : null}

                    {trailer !== null ? (
                        <PillButton className={styles.trailerButton} onClick={onClickPlayTrailer}>
                            <PlayIcon className={styles.icon} />
                            <span className={styles.label}>
                                <FormattedMessage
                                    defaultMessage="Bande-annonce"
                                    description="Button label"
                                />
                            </span>
                        </PillButton>
                    ) : null}
                </div>
            ) : null}
            <div className={styles.content}>
                <div className={styles.inner}>
                    {finalSurtitle !== null && type === 'collection' ? (
                        <Surtitle
                            surtitle={finalSurtitle}
                            placeholder={placeholder}
                            className={styles.surtitle}
                        />
                    ) : null}
                    <HeaderTitle className={styles.title} placeholder={placeholder}>
                        {slug !== null ? (
                            <Link
                                href={
                                    collectionUrl({
                                        type,
                                        slug,
                                    }) || '#'
                                }
                            >
                                {title}
                            </Link>
                        ) : (
                            title
                        )}
                    </HeaderTitle>
                    {finalSurtitle !== null && type !== 'collection' ? (
                        <Surtitle
                            surtitle={finalSurtitle}
                            placeholder={placeholder}
                            className={styles.surtitle}
                        />
                    ) : null}
                    {blocked ? <Blocked unblocked={unblocked} className={styles.blocked} /> : null}
                    {sponsor !== null ? (
                        <Sponsor
                            {...sponsor}
                            withImage
                            className={styles.sponsor}
                            imageClassName={styles.sponsorImage}
                        />
                    ) : null}
                    {broadcasts !== null && broadcasts.length > 0 ? (
                        <Broadcasts
                            broadcasts={broadcasts}
                            type={isPodcast ? 'podcast' : 'video'}
                            className={styles.broadcasts}
                        />
                    ) : null}
                    {description !== null || placeholder ? (
                        <div className={styles.description}>
                            <Body className={styles.body}>
                                <Blocks
                                    blocks={[
                                        placeholder
                                            ? {
                                                  type: 'placeholder',
                                              }
                                            : {
                                                  type: 'text',
                                                  body: description,
                                              },
                                    ]}
                                    withoutMaxWidth
                                    className={styles.blocks}
                                />
                            </Body>
                        </div>
                    ) : null}
                    {pills.length > 0 ? (
                        <div className={styles.pills}>
                            {pills.map(({ label, href }) => (
                                <PillButton key={`pill-${href}`} href={href} bordered compact>
                                    {label}
                                </PillButton>
                            ))}
                        </div>
                    ) : null}
                    {callToAction !== null ? (
                        <PillButton href={ctaUrl} external className={styles.button}>
                            {ctaLabel !== null ? (
                                <span dangerouslySetInnerHTML={{ __html: ctaLabel }} />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="En savoir plus..."
                                    description="Button label"
                                />
                            )}
                        </PillButton>
                    ) : null}
                    {credits !== null && credits.length > 0 ? (
                        <Credits credits={credits} className={styles.credits} />
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export default CollectionSerieHeader;
